import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService } from '../services/payment.service';
import { QUERY_KEYS } from '../constants';
import type { ApiResponse } from '../types/common';
import type { CreatePaymentRequest } from '../types/payment.types';

export const usePaymentStatus = () => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PAYMENT, 'status'],
    queryFn: () => paymentService.getStatus(),
    staleTime: 60 * 1000,
  });
};

export const usePaymentCurrencies = () => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PAYMENT, 'currencies'],
    queryFn: () => paymentService.getCurrencies(),
    staleTime: 5 * 60 * 1000,
  });
};

export const usePaymentMinAmount = (currencyFrom: string, currencyTo: string, enabled = true) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PAYMENT, 'minAmount', currencyFrom, currencyTo],
    queryFn: () => paymentService.getMinAmount(currencyFrom, currencyTo),
    enabled: enabled && !!currencyFrom && !!currencyTo,
    staleTime: 60 * 1000,
  });
};

export const usePaymentEstimate = (
  amount: number,
  currencyFrom: string,
  currencyTo: string,
  enabled = true
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PAYMENT, 'estimate', amount, currencyFrom, currencyTo],
    queryFn: () => paymentService.getEstimate(amount, currencyFrom, currencyTo),
    enabled: enabled && amount > 0 && !!currencyFrom && !!currencyTo,
    staleTime: 60 * 1000,
  });
};

export const useCreatePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreatePaymentRequest) => paymentService.createPayment(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PAYMENT });
    },
  });
};

export const usePaymentStatusById = (paymentId: number | null, enabled = true) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PAYMENT, 'paymentStatus', paymentId],
    queryFn: () => paymentService.getPaymentStatus(paymentId!),
    enabled: enabled && !!paymentId && paymentId > 0,
    refetchInterval: (query) => {
      const status = (query.state.data as ApiResponse<{ payment_status: string }>)?.data?.payment_status;
      return status === 'finished' || status === 'confirmed' ? false : 3000;
    },
  });
};
